import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_WORKDAYS,
  ScheduleStatus,
  ScheduleTaskType,
  WEEKDAY_CODES,
  checkScheduleTasks,
  parseCalendarSchedule,
  scheduleNonWorkDates,
  scheduleTaskWorkdays,
  type ScheduleSourceRef,
  type ScheduleTaskProblem,
} from '@construction/shared';
import { scheduleApi, type Drawing, type ScheduleData } from '@/api/endpoints';
import { Alert, Button, Card, Checkbox, Input, Select, Spinner } from '@/components/ui';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { randomId } from '@/lib/checksum';
import { extractPdfContent } from './extract-pdf';
import { GanttChart } from './GanttChart';

/**
 * 核对表（CR-080）：解析出来的每一项摆成一张可改的表，人对着原件核对，确认了才发布。
 *
 * 客户拍板「上传的人核对后才发布」：解析器只认它见过的版式，年份是推的、类型是按名称猜的 ——
 * 这些都要人过一眼。认不出的文件（图片、别家软件的 PDF）就是一张空表，照着原件手填。
 * 以后 AI 经 MCP 交上来的草稿也落进这张表（2.0）。
 *
 * 表单当场检查和服务端存之前的检查是同一份（shared 的 `checkScheduleTasks`）。
 * 下面带一张甘特图预览，改一格就看得到横条挪到哪。
 */

interface Row {
  key: string;
  id?: string;
  code?: string;
  name: string;
  type: ScheduleTaskType;
  plannedStart: string;
  plannedFinish: string;
  color?: string | null;
  sourceRef?: ScheduleSourceRef[] | null;
}

type Origin =
  | { kind: 'saved'; data: ScheduleData }
  | { kind: 'parsed'; count: number; inferredYear: number | null }
  | { kind: 'unrecognized' }
  | { kind: 'failed'; message: string };

const TYPES = [ScheduleTaskType.WORK, ScheduleTaskType.INSPECTION, ScheduleTaskType.NONWORK];

export function ScheduleReview({
  siteId,
  drawing,
  existing,
  today,
  onOpenOriginal,
  onClose,
}: {
  siteId: number;
  /** 核对的是哪一版进度表文件 */
  drawing: Drawing;
  /** 这一版已经存过的（草稿或已发布）；没有就从原件解析 */
  existing: ScheduleData | null;
  today: string;
  onOpenOriginal: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toMessage = useErrorMessage();
  const queryClient = useQueryClient();

  const [rows, setRows] = useState<Row[]>(() => (existing ? existing.tasks.map((task) => ({ ...task, key: task.id })) : []));
  const [workdays, setWorkdays] = useState<number[]>(() =>
    existing
      ? existing.calendar.workdays.map((code) => WEEKDAY_CODES.indexOf(code as (typeof WEEKDAY_CODES)[number]))
      : [...DEFAULT_WORKDAYS],
  );
  const [parser, setParser] = useState<string | null>(existing?.source.parser ?? null);
  const [origin, setOrigin] = useState<Origin | null>(existing ? { kind: 'saved', data: existing } : null);
  const [showErrors, setShowErrors] = useState(false);

  // ---- 没存过就从原件解析（只跑一次）
  const started = useRef(false);
  useEffect(() => {
    if (existing || started.current) return;
    started.current = true;
    if (drawing.mimeType !== 'application/pdf') {
      setOrigin({ kind: 'unrecognized' });
      setRows([emptyRow()]);
      return;
    }
    extractPdfContent(drawing.url)
      .then((pdf) => {
        const parsed = pdf ? parseCalendarSchedule(pdf.pages, { createdAt: pdf.createdAt, today }) : null;
        if (!parsed || parsed.tasks.length === 0) {
          setOrigin({ kind: 'unrecognized' });
          setRows([emptyRow()]);
          return;
        }
        setRows(parsed.tasks.map((task) => ({ ...task, key: randomId() })));
        setWorkdays(parsed.workdays);
        setParser(parsed.format);
        setOrigin({ kind: 'parsed', count: parsed.tasks.length, inferredYear: parsed.inferredYear });
      })
      .catch((err: unknown) => {
        setOrigin({ kind: 'failed', message: err instanceof Error ? err.message : String(err) });
        setRows([emptyRow()]);
      });
  }, [existing, drawing, today]);

  const check = useMemo(() => checkScheduleTasks(rows), [rows]);
  const problemOf = useMemo(() => new Map(check.rows.map((r) => [r.index, r.problem])), [check]);
  const nonWork = useMemo(() => new Set(scheduleNonWorkDates(rows, workdays)), [rows, workdays]);
  const published = existing?.status === ScheduleStatus.PUBLISHED;

  const save = useMutation({
    mutationFn: (publish: boolean) =>
      scheduleApi.save(siteId, {
        drawingId: drawing.id,
        parser,
        workdays,
        publish,
        tasks: rows.map((row) => ({
          ...(row.id !== undefined ? { id: row.id } : {}),
          name: row.name,
          type: row.type,
          plannedStart: row.plannedStart,
          plannedFinish: row.plannedFinish,
          color: row.color ?? null,
          sourceRef: row.sourceRef ?? null,
        })),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['schedule', siteId] });
      onClose();
    },
  });

  const submit = (publish: boolean) => {
    setShowErrors(true);
    if (check.tooMany || check.rows.length > 0 || (publish && rows.length === 0)) return;
    save.mutate(publish);
  };

  const update = (key: string, patch: Partial<Row>) =>
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  if (origin === null) {
    return (
      <Card padding="lg" className="flex items-center justify-center gap-3 py-16 text-ink-3">
        <Spinner />
        {t('schedule.review.parsing')}
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card padding="md" className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-title font-semibold text-ink">{t('schedule.review.title')}</h2>
            <p className="mt-1 text-body leading-relaxed text-ink-2">{originText(origin, t, published)}</p>
          </div>
          <Button variant="secondary" onClick={onOpenOriginal}>
            {t('schedule.review.openOriginal')}
          </Button>
        </div>

        {/* 工作日历：月历原表里印着 Non-workday 的列已经勾掉了 */}
        <div>
          <p className="mb-1 text-body font-medium text-ink-2">{t('schedule.review.workdays')}</p>
          <div className="flex flex-wrap gap-x-1">
            {[1, 2, 3, 4, 5, 6, 0].map((d) => (
              <Checkbox
                key={d}
                label={t(`schedule.weekday.${WEEKDAY_CODES[d]}`)}
                checked={workdays.includes(d)}
                onChange={(e) =>
                  setWorkdays((prev) =>
                    e.target.checked ? [...prev, d].sort((a, b) => a - b) : prev.length > 1 ? prev.filter((x) => x !== d) : prev,
                  )
                }
              />
            ))}
          </div>
        </div>
      </Card>

      <Card>
        {/* 表头：宽屏才有；窄屏每一行自己带小标题 */}
        <div className="hidden border-b border-rule px-4 py-2 text-caption font-medium text-ink-3 xl:grid xl:grid-cols-[3rem_minmax(0,1fr)_8rem_10rem_10rem_4rem_6rem] xl:gap-3">
          <span>{t('schedule.gantt.col.code')}</span>
          <span>{t('schedule.gantt.col.name')}</span>
          <span>{t('schedule.gantt.col.type')}</span>
          <span>{t('schedule.gantt.col.start')}</span>
          <span>{t('schedule.gantt.col.finish')}</span>
          <span className="text-right">{t('schedule.gantt.col.workdays')}</span>
          <span />
        </div>

        {rows.map((row, index) => {
          const problem = showErrors ? problemOf.get(index) : undefined;
          const days = scheduleTaskWorkdays(row, workdays, nonWork);
          return (
            <div key={row.key} className="border-b border-rule px-4 py-3 last:border-0 xl:py-2">
              <div className="grid grid-cols-2 gap-3 xl:grid-cols-[3rem_minmax(0,1fr)_8rem_10rem_10rem_4rem_6rem] xl:items-center">
                <span className="hidden font-mono text-caption text-ink-3 xl:block">{row.code ?? '—'}</span>
                <label className="col-span-2 xl:col-span-1">
                  <span className="mb-1 block text-caption text-ink-3 xl:hidden">{t('schedule.gantt.col.name')}</span>
                  <Input
                    value={row.name}
                    maxLength={250}
                    aria-invalid={problem === 'nameRequired' || problem === 'nameTooLong'}
                    onChange={(e) => update(row.key, { name: e.target.value })}
                  />
                </label>
                <label className="col-span-2 xl:col-span-1">
                  <span className="mb-1 block text-caption text-ink-3 xl:hidden">{t('schedule.gantt.col.type')}</span>
                  <Select value={row.type} onChange={(e) => update(row.key, { type: e.target.value as ScheduleTaskType })}>
                    {TYPES.map((type) => (
                      <option key={type} value={type}>
                        {t(`schedule.type.${type}`)}
                      </option>
                    ))}
                  </Select>
                </label>
                <label>
                  <span className="mb-1 block text-caption text-ink-3 xl:hidden">{t('schedule.gantt.col.start')}</span>
                  <Input
                    type="date"
                    value={row.plannedStart}
                    aria-invalid={problem === 'startInvalid' || problem === 'order'}
                    onChange={(e) => update(row.key, { plannedStart: e.target.value })}
                  />
                </label>
                <label>
                  <span className="mb-1 block text-caption text-ink-3 xl:hidden">{t('schedule.gantt.col.finish')}</span>
                  <Input
                    type="date"
                    value={row.plannedFinish}
                    aria-invalid={problem === 'finishInvalid' || problem === 'order' || problem === 'spanTooLong'}
                    onChange={(e) => update(row.key, { plannedFinish: e.target.value })}
                  />
                </label>
                <span className="text-body text-ink-2 tabular-nums xl:text-right">
                  <span className="text-caption text-ink-3 xl:hidden">{t('schedule.gantt.col.workdays')} </span>
                  {days ?? '—'}
                </span>
                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    aria-label={t('schedule.review.removeRow', { name: row.name || index + 1 })}
                    onClick={() => setRows((prev) => prev.filter((r) => r.key !== row.key))}
                    className="px-3 text-danger hover:bg-danger/8"
                  >
                    {t('app.delete')}
                  </Button>
                </div>
              </div>
              {problem && (
                <p className="mt-1.5 text-caption font-medium text-danger">{t(`schedule.review.problem.${problem satisfies ScheduleTaskProblem}`)}</p>
              )}
            </div>
          );
        })}

        <div className="border-t border-rule p-3">
          <Button variant="ghost" onClick={() => setRows((prev) => [...prev, emptyRow(prev.at(-1))])}>
            + {t('schedule.review.addRow')}
          </Button>
        </div>
      </Card>

      {showErrors && check.tooMany && <Alert>{t('schedule.review.problem.tooMany')}</Alert>}
      {showErrors && check.rows.length > 0 && (
        <Alert>{t('schedule.review.fixRows', { count: check.rows.length })}</Alert>
      )}
      {save.isError && <Alert>{toMessage(save.error)}</Alert>}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="secondary" onClick={onClose}>
          {t('app.cancel')}
        </Button>
        <div className="flex flex-wrap gap-3">
          {!published && (
            <Button variant="secondary" loading={save.isPending && save.variables === false} onClick={() => submit(false)}>
              {t('schedule.review.saveDraft')}
            </Button>
          )}
          <Button loading={save.isPending && save.variables === true} disabled={rows.length === 0} onClick={() => submit(true)}>
            {published ? t('schedule.review.publishChanges') : t('schedule.review.publish')}
          </Button>
        </div>
      </div>

      {rows.length > 0 && (
        <section>
          <h3 className="mb-2 text-body font-medium text-ink-2">{t('schedule.review.preview')}</h3>
          <GanttChart
            tasks={rows.map((row) => ({ ...row, code: row.code ?? null }))}
            workdays={workdays}
            today={today}
            scale="week"
            colorMode="type"
            columns="lite"
          />
        </section>
      )}
    </div>
  );
}

/** 新加的一行：日期接着上一行往后排，省得每次从日历上翻回来 */
function emptyRow(after?: Row): Row {
  const date = after?.plannedFinish ?? '';
  return { key: randomId(), name: '', type: ScheduleTaskType.WORK, plannedStart: date, plannedFinish: date };
}

function originText(origin: Origin, t: (key: string, opts?: Record<string, unknown>) => string, published: boolean): string {
  switch (origin.kind) {
    case 'saved':
      return published ? t('schedule.review.fromPublished') : t('schedule.review.fromDraft');
    case 'parsed':
      return origin.inferredYear !== null
        ? t('schedule.review.fromParsedYear', { count: origin.count, year: origin.inferredYear })
        : t('schedule.review.fromParsed', { count: origin.count });
    case 'unrecognized':
      return t('schedule.review.unrecognized');
    case 'failed':
      return t('schedule.review.parseFailed', { message: origin.message });
  }
}
